import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { PeopleAlt, TrendingUp } from "@mui/icons-material";
import Groups2Icon from "@mui/icons-material/Groups2";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import CountUp from "react-countup";
import "aos/dist/aos.css";
import AOS from "aos";

// Initialize AOS
AOS.init();

// Styled Components
const Section = styled.section`
  display: flex;
  flex-direction: column;
  padding: 90px 85px 70px 95px;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 34px;

  @media (max-width: 1024px) {
    padding: 100px 50px 0px 50px;
  }

  @media (max-width: 768px) {
    padding: 30px 40px;
  }

  @media (max-width: 480px) {
    padding: 90px 20px;
    gap: 32px;
  }
`;

const Title = styled.h2`
  font-size: 32px;
  color: ${({ theme }) => theme.text_primary};
`;

const AnalyticsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1100px;
`;

const AnalyticsTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 250px;
  padding: 20px;
  background: ${({ theme }) => theme.primary}10;
  border: 1.5px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.text_primary};
  border-radius: 16px;
  text-align: center;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-4px);
    cursor: pointer;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IconWrapper = styled.div`
  font-size: 30px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primary};
`;

const StatNumber = styled.p`
  font-size: 28px;
  font-weight: bold;
  margin: 0;
`;

const StatLabel = styled.p`
  font-size: 16px;
  margin: 0;
  color: ${({ theme }) => theme.text_primary};
`;

const Image = styled.img`
  max-width: 25%; /* Adjust for better visibility */
  max-height: 25%;
  margin-bottom: 5px;
  transition: max-width 0.3s ease, max-height 0.3s ease;

  @media (max-width: 768px) {
    max-width: 70%;
    max-height: 70%;
    margin-bottom: 10px;
  }
  @media (max-width: 480px) {
    max-width: 60%;
    max-height: 60%;
    margin-bottom: 10px;
  }
`;

// Component
const LinkedIn = ({
  followers,
  connections,
  postImpressions,
  posts,
  darkMode,
}) => {
  const [startAnimation, setStartAnimation] = useState(false);
  const [showPlus, setShowPlus] = useState({
    followers: false,
    connections: false,
    impressions: false,
    posts: false,
  });

  useEffect(() => {
    const handleScroll = () => {
      const section = document.querySelector("#linkedin-insights");
      if (section && section.getBoundingClientRect().top < window.innerHeight) {
        setStartAnimation(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Section id="linkedin-insights">
      <Title>LinkedIn Insights</Title>

      {darkMode ? (
        <Image src="LinkedIn.svg" alt="Image" data-aos="fade-right" />
      ) : (
        <Image src="linkedInWhite.svg" alt="Image" data-aos="fade-right" />
      )}

      <AnalyticsContainer>
        {/* Followers */}
        <AnalyticsTile data-aos="fade-up">
          <IconWrapper>
            <Groups2Icon fontSize="inherit" />
          </IconWrapper>
          <StatNumber>
            {startAnimation && (
              <CountUp
                start={0}
                end={followers || 0}
                duration={10}
                onEnd={() =>
                  setShowPlus((prev) => ({ ...prev, followers: true }))
                }
              />
            )}
            {showPlus.followers && "+"}
          </StatNumber>
          <StatLabel>Followers</StatLabel>
        </AnalyticsTile>

        {/* Posts */}
        <AnalyticsTile data-aos="fade-up">
          <IconWrapper>
            <LibraryBooksIcon fontSize="inherit" />
          </IconWrapper>
          <StatNumber>
            {startAnimation && (
              <CountUp
                start={0}
                end={posts || 0}
                duration={10}
                onEnd={() => setShowPlus((prev) => ({ ...prev, posts: true }))}
              />
            )}
            {showPlus.posts && "+"}
          </StatNumber>
          <StatLabel>Posts</StatLabel>
        </AnalyticsTile>

        {/* Post Impressions */}
        <AnalyticsTile data-aos="fade-up" data-aos-delay="400">
          <IconWrapper>
            <TrendingUp fontSize="inherit" />
          </IconWrapper>
          <StatNumber>
            {startAnimation && (
              <CountUp
                start={0}
                end={postImpressions || 0}
                duration={10}
                onEnd={() =>
                  setShowPlus((prev) => ({ ...prev, impressions: true }))
                }
              />
            )}
            {showPlus.impressions && "+"}
          </StatNumber>
          <StatLabel>Post Impressions</StatLabel>
        </AnalyticsTile>

        {/* Connections */}
        <AnalyticsTile data-aos="fade-up" data-aos-delay="200">
          <IconWrapper>
            <PeopleAlt fontSize="inherit" />
          </IconWrapper>
          <StatNumber>
            {startAnimation && (
              <CountUp
                start={0}
                end={connections || 0}
                duration={10}
                onEnd={() =>
                  setShowPlus((prev) => ({ ...prev, connections: true }))
                }
              />
            )}
            {showPlus.connections && "+"}
          </StatNumber>
          <StatLabel>Connections</StatLabel>
        </AnalyticsTile>
      </AnalyticsContainer>
    </Section>
  );
};

export default LinkedIn;
